import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env.local file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  // Turn off realtime entirely. We aren't using it yet and the websocket
  // handshake + heartbeat can add hundreds of ms to first-load time.
  realtime: { params: { eventsPerSecond: 0 } },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Use implicit flow instead of the supabase-js v2 default (PKCE).
    //
    // Why: PKCE is great for browser apps where the user signs UP and
    // signs IN in the SAME browser session — the SDK stores a
    // `code_verifier` in localStorage at sign-up, then proves
    // possession of it during the token exchange at sign-in. That
    // security model breaks down completely for email-invite flows:
    //
    //   * Firm admin generates the invite from their laptop browser.
    //   * Client receives the email on their phone.
    //   * Client taps the link, lands on docketflowapp.com.
    //   * Supabase tries to exchange ?code=... for a session, but
    //     the verifier lives on the LAPTOP'S localStorage, not the
    //     PHONE'S. The exchange fails silently — no SIGNED_IN event,
    //     no error event, just an indefinite spinner.
    //
    // Implicit flow puts the access_token + refresh_token directly
    // into the URL fragment (#access_token=...). Any browser that
    // can read the link can establish the session. No verifier
    // needed. The trade-off — tokens briefly visible in the URL —
    // is acceptable for a B2B law-firm SaaS (we control both the
    // sender and the redirect domain; we're not exposing this on a
    // public iframe).
    flowType: 'implicit',
    // Bypass the storage lock entirely. We don't use multi-tab token refresh
    // racing, and the lock causes orphaned-lock warnings both in development
    // (React StrictMode double-invokes effects) and in production (cold-start
    // delays cause the 5s lock window to expire before the session resolves).
    lock: (_name, _acquireTimeout, fn) => fn(),
  },
});

// ---- query timing + error capture -------------------------------------------
//
// Wrap fetch in two modes:
//
//   * Development: log every Supabase request with its duration so you can
//     spot slow queries / cold starts in the console.
//   * Production: silent on success, but capture any fetch-level failure
//     (network down, CORS, DNS, timeout) to the structured error log so
//     support has something to act on. The Supabase JS SDK turns these
//     into `{ error: { message: 'fetch failed' } }` responses and never
//     throws — meaning window.onerror / unhandledrejection don't see them
//     either. Without this hook they'd be completely invisible.
const origFetch = window.fetch.bind(window);
const isDev     = process.env.NODE_ENV !== 'production';

window.fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input.url;
  if (!url || !url.includes(new URL(supabaseUrl).host)) return origFetch(input, init);
  const t0     = performance.now();
  const method = (init?.method || 'GET').toUpperCase();
  try {
    const res = await origFetch(input, init);
    if (isDev) {
      const ms = Math.round(performance.now() - t0);
      const path = url.replace(supabaseUrl, '').split('?')[0];
      const tag  = `%c[supabase ${ms}ms]`;
      const style = ms > 1000 ? 'color:red;font-weight:bold'
                  : ms > 300  ? 'color:orange'
                  :             'color:gray';
      // eslint-disable-next-line no-console
      console.log(tag, style, method, path);
    }
    return res;
  } catch (err) {
    // Fetch-level failure (network, CORS, timeout). Log structurally
    // in both dev and prod — this is the class of error that otherwise
    // would silently surface as "the save didn't work" with no trail.
    try {
      // Lazy-load so circular imports can't bite us during the
      // module-init phase where supabaseClient is loaded first.
      // eslint-disable-next-line global-require
      const { logError } = require('../lib/errorLog');
      const path = url.replace(supabaseUrl, '').split('?')[0];
      logError(err, { source: 'supabase-fetch', method, path });
    } catch { /* never let logging break the request path */ }
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error('[supabase FAIL]', method, url, err);
    }
    throw err;
  }
};
